import { SurveyQuestion, QuestionType, QuestionOption } from '../config/surveys/schema';
import { EMOJI_SCALE } from '../config/surveys/defaults';

/**
 * Default options registry for question types that have standard defaults.
 * This allows the auto-complete to work even when questions don't have
 * explicit options defined (they use component-level defaults).
 *
 * To add support for new question types with defaults:
 * 1. Add the default options to this registry
 * 2. Add a generator function in generateTestAnswer
 */
const DEFAULT_OPTIONS: Partial<Record<QuestionType, QuestionOption[]>> = {
  'emoji-scale': EMOJI_SCALE,
};

/**
 * Gets options for a question, falling back to defaults if not explicitly defined.
 */
function getQuestionOptions(question: SurveyQuestion): QuestionOption[] {
  if (question.options && question.options.length > 0) {
    return question.options;
  }
  return DEFAULT_OPTIONS[question.type] || [];
}

/**
 * Picks a random option value from an array of options.
 */
function pickRandomOption(options: QuestionOption[]): string {
  if (options.length === 0) return '';
  const randomIndex = Math.floor(Math.random() * options.length);
  return options[randomIndex].value;
}

/**
 * Generates a test answer for a given question based on its type.
 * This utility is designed to be extensible for future question types.
 *
 * To add support for a new question type:
 * 1. Add a generator function to the generators record below
 * 2. If the type has default options, add them to DEFAULT_OPTIONS above
 */
export function generateTestAnswer(question: SurveyQuestion): string {
  const generators: Record<QuestionType, (q: SurveyQuestion) => string> = {
    'emoji-scale': (q) => {
      const options = getQuestionOptions(q);
      return pickRandomOption(options);
    },
    'radio': (q) => {
      const options = getQuestionOptions(q);
      return pickRandomOption(options);
    },
    'textarea': () => {
      const testResponses = [
        'This is an auto-generated test response for debugging purposes.',
        'Test input for survey validation.',
        'Debug mode: auto-completed response.',
      ];
      return testResponses[Math.floor(Math.random() * testResponses.length)];
    },
  };

  const generator = generators[question.type];
  if (generator) {
    return generator(question);
  }

  // Fallback for unknown question types
  console.warn(`Unknown question type: ${question.type}`);
  return '';
}

/**
 * Generates test answers for all questions in a survey.
 * Returns a Record mapping question IDs to their generated answers.
 */
export function generateAllTestAnswers(
  questions: SurveyQuestion[]
): Record<string, string> {
  const answers: Record<string, string> = {};

  for (const question of questions) {
    // Generate answer for all questions, including optional ones
    const answer = generateTestAnswer(question);
    if (answer) {
      answers[question.id] = answer;
    }
  }

  return answers;
}

/**
 * Check if debug mode is enabled via environment variable.
 * In Create React App, env vars must be prefixed with REACT_APP_.
 */
export function isDebugMode(): boolean {
  const debugValue = process.env.REACT_APP_DEBUG;
  return debugValue?.toLowerCase() === 'true';
}
